import { type ReactNode, type Ref, useMemo } from 'react';
import { ResponsiveGridLayout, useContainerWidth } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';

import {
  CHART_COLS,
  GRID_BREAKPOINTS,
  KPI_COLS,
  compareTableExtraRows,
  type GridBreakpoint,
} from './default-layouts';
import { usePersistedLayout } from './use-persisted-layout';
import { Widget } from './Widget';
import type { LayoutZone } from '@/persistence/config-db';
import { useModeStore } from '@/store/mode-store';
import { useWidgetRuntimeStore } from '@/store/widget-runtime-store';
import type { WidgetConfig, WidgetKind } from '@/types/widget';
import type { FilterExpression } from '@/types/query';

type DashboardGridProps = {
  datasetId: string | null;
  widgets: WidgetConfig[];
  filters: FilterExpression[];
  onRemove: (id: string) => void;
  /** Static children rendered below the grid zones (e.g. DataTable). */
  footer?: ReactNode;
};

const KPI_KIND = new Set<WidgetKind>(['kpi']);
// Chart-zone kinds participate in the 2-column RGL layout. Compare-table
// widgets live in the same RGL so they're draggable alongside charts;
// their footprint expands to the full row via per-kind dims in
// `default-layouts.ts` (item 3).
const CHART_ZONE_KINDS = new Set<WidgetKind>([
  'bar',
  'line',
  'pie',
  'compare-table',
]);

/**
 * Estimate how many extra grid rows a widget needs to host its note without
 * crowding out the chart area. The footer always occupies at least one
 * row's worth of vertical space (border-t + padding + one text line), so
 * any note guarantees +1 row; multi-line notes scale from there.
 */
function noteExtraRows(note?: string): number {
  if (!note) return 0;
  const lineCount = note.split('\n').length;
  return Math.max(1, Math.ceil(lineCount / 2));
}

/**
 * Dashboard shell: filter bar (outside) → KPI zone → chart zone → footer.
 * Each zone is an independent `ResponsiveGridLayout` so widgets can only
 * move within their own tier. Widgets are driven by the widgets store.
 */
export function DashboardGrid({
  datasetId,
  widgets,
  filters,
  onRemove,
  footer,
}: DashboardGridProps): JSX.Element {
  const mode = useModeStore((s) => s.mode);
  const kpiWidgets = useMemo(
    () => widgets.filter((w) => KPI_KIND.has(w.kind)),
    [widgets],
  );
  const chartWidgets = useMemo(
    () => widgets.filter((w) => CHART_ZONE_KINDS.has(w.kind)),
    [widgets],
  );

  // Live row counts per compare-table widget (max of winners + losers).
  // Subscribing keeps the layout snug to the data — when a reload
  // produces fewer rows the cell shrinks; more rows and the cell grows.
  // Falls back to the widget's `limit` until the first query resolves
  // so the very first paint isn't artificially tiny.
  const compareTableRows = useWidgetRuntimeStore((s) => s.compareTableRows);

  const extraRowsById = useMemo(() => {
    const map: Record<string, number> = {};
    for (const w of widgets) {
      let extra = noteExtraRows(w.note);
      if (w.kind === 'compare-table') {
        const liveRows = compareTableRows[w.id];
        const rows = liveRows !== undefined ? liveRows : (w.limit ?? 10);
        extra += compareTableExtraRows(rows);
      }
      map[w.id] = extra;
    }
    return map;
  }, [widgets, compareTableRows]);

  return (
    <div className="flex flex-col gap-3">
      <GridZone
        zone="kpi"
        datasetId={datasetId}
        mode={mode}
        cols={KPI_COLS}
        rowHeight={60}
        margin={[12, 12]}
        widgets={kpiWidgets}
        filters={filters}
        onRemove={onRemove}
        extraRowsById={extraRowsById}
      />
      <GridZone
        zone="chart"
        datasetId={datasetId}
        mode={mode}
        cols={CHART_COLS}
        rowHeight={80}
        margin={[12, 12]}
        widgets={chartWidgets}
        filters={filters}
        onRemove={onRemove}
        extraRowsById={extraRowsById}
      />
      {footer}
    </div>
  );
}

type GridZoneProps = {
  zone: LayoutZone;
  datasetId: string | null;
  mode: 'normal' | 'compare';
  cols: Record<GridBreakpoint, number>;
  rowHeight: number;
  margin: readonly [number, number];
  widgets: WidgetConfig[];
  filters: FilterExpression[];
  onRemove: (id: string) => void;
  extraRowsById: Readonly<Record<string, number>>;
};

function GridZone({
  zone,
  datasetId,
  mode,
  cols,
  rowHeight,
  margin,
  widgets,
  filters,
  onRemove,
  extraRowsById,
}: GridZoneProps): JSX.Element | null {
  const widgetIds = useMemo(() => widgets.map((w) => w.id), [widgets]);
  const widgetKindById = useMemo(() => {
    const m: Record<string, WidgetKind> = {};
    for (const w of widgets) m[w.id] = w.kind;
    return m;
  }, [widgets]);
  const { width, containerRef, mounted } = useContainerWidth({
    initialWidth: 1200,
  });
  const { layouts, onLayoutChange, loaded } = usePersistedLayout(
    datasetId,
    mode,
    zone,
    widgetIds,
    extraRowsById,
    widgetKindById,
  );

  if (widgets.length === 0) {
    return <div ref={containerRef as Ref<HTMLDivElement>} />;
  }

  return (
    <div ref={containerRef as Ref<HTMLDivElement>}>
      {mounted && loaded && (
        <ResponsiveGridLayout<GridBreakpoint>
          width={width}
          layouts={layouts}
          breakpoints={GRID_BREAKPOINTS}
          cols={cols}
          rowHeight={rowHeight}
          margin={margin}
          containerPadding={[0, 0] as const}
          onLayoutChange={onLayoutChange}
          dragConfig={{
            enabled: true,
            handle: '.widget-drag-handle',
            threshold: 3,
            bounded: false,
          }}
          resizeConfig={{ enabled: false, handles: [] }}
          autoSize
        >
          {widgets.map((w) => (
            <div key={w.id} className="h-full">
              <Widget widget={w} filters={filters} onRemove={onRemove} />
            </div>
          ))}
        </ResponsiveGridLayout>
      )}
    </div>
  );
}
